import { Box, Chip, Link, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router'
import {
  WQIBand,
  WaterQualityIndexSelectors,
} from '../../../../store/waterQualityIndex'

type ChipVariant = 'success' | 'warning' | 'error'

const chipSx: Record<ChipVariant, Record<string, string>> = {
  success: { color: 'success.dark', borderColor: 'success.main', bgcolor: 'success.light' },
  warning: { color: 'warning.dark', borderColor: 'warning.main', bgcolor: 'warning.light' },
  error: { color: 'error.main', borderColor: 'error.main', bgcolor: 'error.light' },
}

const bandToChipVariant = (band: WQIBand): ChipVariant | null => {
  switch (band) {
    case 'A':
    case 'B':
    case 'Low Risk':
      return 'success'
    case 'C':
    case 'Moderate Risk':
      return 'warning'
    case 'D':
    case 'F':
    case 'High Risk':
    case 'Very High Risk':
      return 'error'
    default:
      return null
  }
}

const bandLabel = (band: WQIBand): string => band

const StatBlock = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
      {label}
    </Typography>
    {children}
  </Stack>
)

export const UtilityCard = () => {
  const utility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const reputation = useSelector(WaterQualityIndexSelectors.selectUtilityReputation)

  const utilityName = utility?.name ?? 'Unknown'

  // Reputation block
  let reputationNode: React.ReactNode
  if (reputation.score === null || reputation.band === null) {
    reputationNode = (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" color="text.disabled">
          No data available
        </Typography>
        <Chip
          variant="outlined"
          size="small"
          label="No data"
          sx={{ color: 'text.disabled', borderColor: 'divider' }}
        />
      </Stack>
    )
  } else {
    const variant = bandToChipVariant(reputation.band)
    reputationNode = (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          {bandLabel(reputation.band)}
        </Typography>
        {variant && (
          <Chip
            variant="outlined"
            size="small"
            label={bandLabel(reputation.band)}
            sx={chipSx[variant]}
          />
        )}
      </Stack>
    )
  }

  // Violations block
  let violationsNode: React.ReactNode
  const violationCount = reputation.violationCount
  if (violationCount === null) {
    violationsNode = (
      <Typography variant="body2" color="text.disabled">
        No data available
      </Typography>
    )
  } else if (violationCount === 0) {
    violationsNode = (
      <Typography variant="body2" fontWeight={600} color="success.main">
        None on record
      </Typography>
    )
  } else {
    const variant: ChipVariant = violationCount >= 3 ? 'error' : 'warning'
    violationsNode = (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          {`${violationCount} in past 5 years`}
        </Typography>
        <Chip
          variant="outlined"
          size="small"
          label={`${violationCount}`}
          sx={chipSx[variant]}
        />
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        YOUR UTILITY
      </Typography>
      <Stack spacing={2}>
        <StatBlock label="UTILITY">
          <Typography variant="body2" fontWeight={600}>
            {utilityName}
          </Typography>
        </StatBlock>
        <StatBlock label="REPUTATION">{reputationNode}</StatBlock>
        <StatBlock label="VIOLATIONS">{violationsNode}</StatBlock>
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <Link
          component={RouterLink}
          to="/profile/info"
          variant="caption"
          sx={{ color: 'primary.dark', textDecoration: 'none', fontWeight: 600 }}
        >
          View utility info →
        </Link>
      </Box>
    </Box>
  )
}
